"use client";

import Image from "next/image";
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import {
  TrendingUp,
  TrendingDown,
  CircleArrowUp,
  CircleArrowDown,
  ArrowUpRight,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { AedSymbol } from "@/components/ui/AedSymbol";
import { DownloadAppLink } from "@/components/ui/DownloadAppLink";
import { cn } from "@/lib/utils";
import {
  GOLD_RATE_AED_PER_GRAM,
  GOLD_SELL_SPREAD,
  SILVER_RATE_AED_PER_GRAM,
  SILVER_SELL_SPREAD,
} from "@/lib/rates";
import type { LiveRates } from "@/lib/liveRates";
import { useLiveRatesContext } from "@/components/providers/LiveRatesProvider";

type Metal = "gold" | "silver";

type RatePair = {
  metal: Metal;
  label: string;
  purity: string;
  image: string;
  imageAlt: string;
  buy: number;
  sell: number;
  buyUp: boolean;
  sellUp: boolean;
};

// ─── Base rates (live feed, falling back to demo constants) ──────────────────
function buildBaseRates(liveRates?: LiveRates): RatePair[] {
  const goldBuy = liveRates?.goldBuy ?? GOLD_RATE_AED_PER_GRAM;
  const goldSell = liveRates?.goldSell ?? GOLD_RATE_AED_PER_GRAM - GOLD_SELL_SPREAD;
  const silverBuy = liveRates?.silverBuy ?? SILVER_RATE_AED_PER_GRAM;
  const silverSell = liveRates?.silverSell ?? SILVER_RATE_AED_PER_GRAM - SILVER_SELL_SPREAD;
  const goldUp = (liveRates?.goldChangePercent ?? 0) >= 0;
  const silverUp = (liveRates?.silverChangePercent ?? 0) >= 0;

  return [
    {
      metal: "gold",
      label: "Gold",
      purity: "24K · per gram",
      image: "/live-rates-gold.png",
      imageAlt: "24K gold bars",
      buy: goldBuy,
      sell: goldSell,
      buyUp: goldUp,
      sellUp: goldUp,
    },
    {
      metal: "silver",
      label: "Silver",
      purity: "999 · per gram",
      image: "/live-rates-silver.png",
      imageAlt: "999 fine silver bar",
      buy: silverBuy,
      sell: silverSell,
      buyUp: silverUp,
      sellUp: silverUp,
    },
  ];
}

// ─── Framer variants ──────────────────────────────────────────────────────────
const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.06 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ─── RateGroup card ───────────────────────────────────────────────────────────
function RateGroup({ pair }: { pair: RatePair }) {
  const isGold = pair.metal === "gold";

  return (
    <motion.div
      variants={fadeUp(false)}
      whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(14,58,138,0.08)" }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className="group relative overflow-hidden rounded-card border border-[#e3ecfb] bg-white p-5 transition-colors duration-300 hover:border-primary-200"
    >
      {/* Header: label + image */}
      <div className="relative z-10 flex items-start justify-between gap-3">
        <div>
          <p
            className={cn(
              "text-[10px] font-semibold uppercase tracking-[0.16em]",
              isGold ? "text-accent-600" : "text-primary-500"
            )}
          >
            {isGold ? "Gold Rates" : "Silver Rates"}
          </p>
          <h3 className="mt-1 text-[22px] font-bold leading-tight text-primary-900">
            {pair.label}
          </h3>
          <p className="mt-0.5 text-xs font-medium text-text-muted">
            {pair.purity}
          </p>
        </div>

        <Image
          src={pair.image}
          alt={pair.imageAlt}
          width={904}
          height={904}
          sizes="120px"
          className="h-[90px] w-auto object-contain"
        />
      </div>

      {/* Buy / Sell */}
      <div className="relative z-10 mt-4 grid grid-cols-2 divide-x divide-[#e3ecfb] rounded-card-sm border border-[#e3ecfb] bg-[#f7faff]">
        {(["buy", "sell"] as const).map((side) => {
          const value = side === "buy" ? pair.buy : pair.sell;
          const isUp = side === "buy" ? pair.buyUp : pair.sellUp;
          const TrendIcon = isUp ? TrendingUp : TrendingDown;

          return (
            <div key={side} className="px-4 py-3">
              <div className="flex items-center gap-1.5">
                {side === "buy" ? (
                  <CircleArrowUp className="h-4 w-4 text-primary-600" strokeWidth={2} />
                ) : (
                  <CircleArrowDown className="h-4 w-4 text-primary-600" strokeWidth={2} />
                )}
                <p className="text-sm font-semibold text-primary-800">
                  {side === "buy" ? "Buy" : "Sell"}
                </p>
                <span className={cn(
                  "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                  isUp
                    ? "bg-success-bg text-success"
                    : "bg-danger-bg text-danger"
                )}>
                  <TrendIcon
                    className="h-2.5 w-2.5"
                    strokeWidth={2.5}
                    aria-label={isUp ? "Price trending up" : "Price trending down"}
                  />
                  {isUp ? "+0.3%" : "-0.2%"}
                </span>
              </div>
              <div className="mt-1.5 flex items-baseline gap-1">
                <AedSymbol
                  className="h-[0.72em] w-[0.72em] text-primary-700"
                  label="AED "
                />
                <motion.span
                  key={`${pair.metal}-${side}-${value}`}
                  initial={{ y: -4, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.35, ease: [0.2, 0, 0, 1] }}
                  className="font-heading text-[22px] font-bold leading-none text-text-heading tabular-nums"
                >
                  {value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </motion.span>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ─── Gold rate by purity ──────────────────────────────────────────────────────
// 24K is what we sell. The other purities are shown only so customers can
// compare against souk quotes — priced as a fraction of the same live 24K
// rate, never a separate hardcoded feed.
type PriceUnit = "gram" | "tola" | "ounce";

const UNITS: Array<{ id: PriceUnit; label: string; grams: number }> = [
  { id: "gram", label: "Per gram", grams: 1 },
  { id: "tola", label: "Per tola", grams: 11.664 },
  { id: "ounce", label: "Per ounce", grams: 31.1035 },
];

const PURITIES = [
  { karat: "24K (999.9)", fraction: 1, use: "What we sell. Bars, coins, pure savings" },
  { karat: "22K (916)", fraction: 22 / 24, use: "Most common for jewellery in the UAE" },
  { karat: "21K (875)", fraction: 21 / 24, use: "Popular for Arabic gold jewellery" },
  { karat: "18K (750)", fraction: 18 / 24, use: "Harder wearing, used in fine jewellery" },
  { karat: "14K (585)", fraction: 14 / 24, use: "Occasional, mostly imported jewellery" },
];

export function PurityTable() {
  const liveRates = useLiveRatesContext();
  const [unit, setUnit] = useState<PriceUnit>("tola");

  const goldBuy = liveRates?.goldBuy ?? GOLD_RATE_AED_PER_GRAM;
  const goldSell = liveRates?.goldSell ?? GOLD_RATE_AED_PER_GRAM - GOLD_SELL_SPREAD;
  const changePercent = liveRates?.goldChangePercent ?? 0;
  const isUp = changePercent >= 0;
  const grams = UNITS.find((u) => u.id === unit)?.grams ?? 1;

  return (
    <div>
      <h3 className="text-[clamp(1.35rem,2.4vw,1.75rem)] font-semibold leading-tight text-primary-900">
        Gold rate by purity
      </h3>
      <p className="mt-3 max-w-[720px] text-base leading-relaxed text-text-body">
        24K is what we sell. The other purities are shown because they are
        what the souk quotes, so you can compare like for like.
      </p>

      <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Choose a price unit">
        {UNITS.map(({ id, label }) => {
          const active = id === unit;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={active}
              onClick={() => setUnit(id)}
              className={cn(
                "min-h-11 rounded-card-sm border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40",
                active
                  ? "border-primary-800 bg-primary-800 text-white"
                  : "border-[#e3ecfb] bg-white text-primary-800 hover:border-primary-200 hover:bg-primary-50"
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 overflow-hidden rounded-card border border-[#e3ecfb]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead className="bg-primary-800 text-xs font-semibold uppercase tracking-[0.12em] text-white">
              <tr>
                <th scope="col" className="px-5 py-4 sm:px-6">Purity</th>
                <th scope="col" className="px-5 py-4 sm:px-6">Buy</th>
                <th scope="col" className="px-5 py-4 sm:px-6">Sell</th>
                <th scope="col" className="px-5 py-4 sm:px-6">Change today</th>
                <th scope="col" className="px-5 py-4 sm:px-6">What it&apos;s used for</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e3ecfb] bg-white text-base text-text-body">
              {PURITIES.map(({ karat, fraction, use }) => {
                const buy = goldBuy * fraction * grams;
                const sell = goldSell * fraction * grams;
                return (
                  <tr key={karat}>
                    <th scope="row" className="whitespace-nowrap px-5 py-5 font-semibold text-primary-800 sm:px-6">
                      {karat}
                    </th>
                    <td className="whitespace-nowrap px-5 py-5 sm:px-6">
                      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                        <AedSymbol className="h-[0.76em] w-[0.76em]" label="AED " />
                        {buy.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-5 py-5 sm:px-6">
                      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
                        <AedSymbol className="h-[0.76em] w-[0.76em]" label="AED " />
                        {sell.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td
                      className={cn(
                        "whitespace-nowrap px-5 py-5 font-medium sm:px-6",
                        isUp ? "text-success" : "text-danger"
                      )}
                    >
                      <span className="inline-flex items-center gap-1">
                        {isUp ? (
                          <ArrowUp className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <ArrowDown className="h-4 w-4" aria-hidden="true" />
                        )}
                        {Math.abs(changePercent).toFixed(1)}%
                      </span>
                    </td>
                    <td className="min-w-[240px] px-5 py-5 text-text-muted sm:px-6">
                      {use}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-text-muted">
        1 tola = 11.664g · 1 troy ounce = 31.1035g. Rates are derived from
        JustGold&apos;s live 24K gold price; other purities are shown for
        comparison only — JustGold buys and sells 24K.
      </p>
    </div>
  );
}

// ─── Section root ─────────────────────────────────────────────────────────────
export function LiveRatesSection({
  headingLevel = "h2",
  showPurityRates = false,
}: {
  headingLevel?: "h1" | "h2";
  showPurityRates?: boolean;
}) {
  const reduce = useReducedMotion() ?? false;
  const liveRates = useLiveRatesContext();
  const rates = useMemo(() => buildBaseRates(liveRates), [liveRates]);
  const Heading = headingLevel;

  return (
    <motion.section
      className="relative overflow-hidden border-t border-[#e3ecfb] bg-[#f7faff]"
      aria-labelledby="live-rates-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
    >
      <div className="relative mx-auto max-w-[1440px] px-6 pb-16 pt-16 sm:px-10 sm:pb-20 sm:pt-20 lg:px-20 lg:pb-24 lg:pt-24">
        {/* Heading */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mb-9 rounded-card bg-primary-900 px-6 py-6 sm:px-8 sm:py-7"
        >
          <p className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-300">
            <span className="h-px w-4 bg-accent-300" aria-hidden="true" />
            Live Rates
          </p>
          <Heading
            id="live-rates-heading"
            className="text-[clamp(1.5rem,3vw,2.15rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-white"
          >
            Today&apos;s gold and silver rates in Dubai
          </Heading>
        </motion.div>
        <motion.p
          variants={fadeUp(reduce)}
          className="-mt-4 mb-9 max-w-[560px] text-lg leading-[1.45] text-text-body"
        >
          Transparent buy and sell prices for 24K gold and fine silver,
          updated in real time.
        </motion.p>

        {/* Rate cards */}
        <motion.div variants={container} className="grid gap-5 sm:grid-cols-2">
          {rates.map((pair) => (
            <RateGroup key={pair.metal} pair={pair} />
          ))}
        </motion.div>

        {showPurityRates && (
          <motion.div variants={fadeUp(reduce)} className="mt-12">
            <PurityTable />
          </motion.div>
        )}

        {/* Conversion point: users have just compared the current buy and sell prices. */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mt-5 flex flex-col gap-4 rounded-card bg-primary-800 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6"
        >
          <div>
            <p className="text-base font-semibold text-white">
              Ready to own real 24K gold?
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-1 text-sm text-primary-200">
              Buy, hold, or redeem with confidence. Physically backed, vaulted
              and insured in Dubai.
            </p>
          </div>
          <DownloadAppLink className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-[10px] bg-white px-5 py-3 text-sm font-semibold text-primary-900 transition-colors duration-200 hover:bg-accent-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60">
            Buy gold in the app
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
          </DownloadAppLink>
        </motion.div>
      </div>
    </motion.section>
  );
}
